/*
  Purchase Options — "2. How do you want it?"

  Rewritten for the variant-per-pack model. Every pack is its own variant with
  its own selling plan allocation, so there is no tier resolution and no
  theme-side discount table left: the price on each row is whatever Shopify and
  Recharge report for the selected variant.

  The Pack Picker owns the variant. It publishes `pack-picker:change` with the
  variant id, box count and both prices; this block displays them, unlocks the
  gifts by box count, and writes the selling plan into the cart form. It answers
  with `purchase-options:change` so the Pack Picker can reprice its cards for
  one-time vs subscription.
*/
(() => {
  const SELECTOR = '[data-purchase-options-root]';
  const GIFT_TOTAL_FALLBACK = 2;

  class PurchaseOptions {
    constructor(root) {
      this.root = root;
      this.config = this.readConfig();
      this.variants = [...root.querySelectorAll('[data-purchase-options-variant]')];
      this.gifts = [...root.querySelectorAll('[data-purchase-options-gift]')];
      this.planCache = new WeakMap();

      // Shopify refuses a one-time line for a subscription-only product.
      this.requiresPlan = this.config.requiresSellingPlan === true;

      this.state = {
        purchase: this.requiresPlan || this.config.defaultPurchase !== 'otp' ? 'sub' : 'otp',
        variantId: String(this.config.initialVariantId || ''),
        boxes: 1,
      };

      this.neutralizeLegacyPlanInput();
      this.syncFromVariant(this.state.variantId);
      this.bind();
      this.selectPurchase(this.state.purchase);
    }

    readConfig() {
      try { return JSON.parse(this.root.querySelector('[data-purchase-options-config]').textContent); }
      catch (_) { return {}; }
    }

    get form() {
      return this.config.productFormId ? document.getElementById(this.config.productFormId) : null;
    }

    // buy-buttons.liquid ships a hidden `input[name="selling_plan"]` pre-filled
    // with a plan id, and product-form.js keeps rewriting it. Dropping its name
    // keeps it out of the submitted data so this element owns the field.
    neutralizeLegacyPlanInput() {
      if (document.querySelector('.product_with_subscription .purchase_subscribe')) {
        if (!this.legacyWarned) {
          this.legacyWarned = true;
          console.warn('[purchase-options] The legacy .purchase_subscribe subscription UI is also on this page, so it keeps ownership of selling_plan. Remove it to let Purchase Options drive the form.');
        }
        this.root.querySelector('[data-purchase-options-plan]')?.removeAttribute('name');
        return;
      }
      document.querySelectorAll('input[name="selling_plan"]').forEach(input => {
        input.dataset.purchaseOptionsReleasedName = 'selling_plan';
        input.removeAttribute('name');
      });
    }

    variantNode(variantId) {
      const id = String(variantId || '');
      return this.variants.find(node => node.dataset.variantId === id) || this.variants[0] || null;
    }

    plansFor(node) {
      if (!node) return [];
      if (this.planCache.has(node)) return this.planCache.get(node);
      let plans = [];
      try { plans = JSON.parse(node.querySelector('[data-purchase-options-selling-plans]')?.textContent || '[]'); }
      catch (_) { plans = []; }
      this.planCache.set(node, plans);
      return plans;
    }

    // The plan the shopper picked in the frequency dropdown, else the first one
    // this variant offers.
    currentPlan(node) {
      const plans = this.plansFor(node);
      if (!plans.length) return null;
      const select = this.root.querySelector('[data-purchase-options-frequency]');
      const chosen = select && plans.find(plan => String(plan.id) === String(select.value));
      return chosen || plans[0];
    }

    // Prices come from the Pack Picker's event when there is one; otherwise read
    // them straight off the variant so this block still works on its own.
    syncFromVariant(variantId) {
      const node = this.variantNode(variantId);
      if (!node) return;
      const plan = this.currentPlan(node);
      this.state.variantId = node.dataset.variantId;
      this.state.boxes = Math.max(1, parseInt(node.dataset.boxes, 10) || 1);
      this.prices = {
        otp: Number(node.dataset.price) || 0,
        sub: plan ? Number(plan.price) || 0 : Number(node.dataset.price) || 0,
        full: this.prices?.full || 0,
      };
    }

    bind() {
      this.root.addEventListener('click', event => {
        if (event.target.closest('select')) return;
        const mode = event.target.closest('[data-purchase-options-purchase]');
        if (mode) this.selectPurchase(mode.dataset.purchaseOptionsPurchase);
      });

      this.root.addEventListener('keydown', event => {
        if (event.target.tagName === 'SELECT') return;
        if (event.key !== 'Enter' && event.key !== ' ') return;
        const mode = event.target.closest('[data-purchase-options-purchase]');
        if (!mode) return;
        event.preventDefault();
        this.selectPurchase(mode.dataset.purchaseOptionsPurchase);
      });

      this.root.addEventListener('change', event => {
        if (!event.target.matches('[data-purchase-options-frequency]')) return;
        this.syncFromVariant(this.state.variantId);
        this.render();
        this.announce();
      });

      document.addEventListener('pack-picker:change', event => {
        const detail = event.detail || {};
        const boxes = Math.max(1, Number(detail.boxes) || 1);
        this.applyPackDefaultInterval(boxes);
        if (detail.variantId) this.syncFromVariant(detail.variantId);
        this.state.boxes = boxes;
        // The picker knows the full (buy-one-box-at-a-time) price the savings
        // percentage is measured against. The subscription price is NOT taken
        // from it: it only knows the first plan, and intervals can differ.
        this.prices.full = Number(detail.fullPrice) || 0;
        this.render();
        this.announce();
      });

      window.addEventListener('pageshow', () => this.render());
    }

    selectPurchase(type) {
      const next = this.requiresPlan ? 'sub' : (type === 'sub' ? 'sub' : 'otp');
      this.state.purchase = next;

      this.root.querySelectorAll('[data-purchase-options-purchase]').forEach(mode => {
        mode.setAttribute('aria-checked', String(mode.dataset.purchaseOptionsPurchase === next));
      });

      this.render();
      this.announce();
    }

    // Each pack can open on its own interval (the mockup's 30 / 60 / 90 days by
    // box count). Off unless set in the block, and only ever picks an interval
    // the product actually offers.
    applyPackDefaultInterval(boxes) {
      const days = Number(this.config.packDays?.[String(boxes)]) || 0;
      const select = this.root.querySelector('[data-purchase-options-frequency]');
      if (!days || !select) return;
      const option = [...select.options].find(o => Number(o.dataset.days) === days);
      if (option) select.value = option.value;
    }

    currentPlanId() {
      const plan = this.currentPlan(this.variantNode(this.state.variantId));
      return plan ? String(plan.id) : null;
    }

    announce() {
      document.dispatchEvent(new CustomEvent('purchase-options:change', {
        bubbles: true,
        detail: { purchase: this.state.purchase, price: this.currentPrice(), planId: this.currentPlanId() },
      }));
    }

    currentPrice() {
      return this.state.purchase === 'sub' ? this.prices.sub : this.prices.otp;
    }

    render() {
      this.setText('[data-purchase-options-sub-price]', this.formatMoney(this.prices.sub));
      this.setText('[data-purchase-options-otp-price]', this.formatMoney(this.prices.otp));
      this.renderGifts();
      this.syncPlanField();
      this.root.dataset.purchaseOptionsState = this.state.purchase;
    }

    renderGifts() {
      const chip = this.root.querySelector('[data-purchase-options-chip]');
      const unlockedNode = this.root.querySelector('[data-purchase-options-unlocked]');
      const bar = this.root.querySelector('[data-purchase-options-bar]');
      const worth = this.root.querySelector('[data-purchase-options-worth]');

      let unlocked = 0;
      let value = 0;

      this.gifts.forEach(gift => {
        const need = Math.max(1, parseInt(gift.dataset.giftMinBoxes, 10) || 1);
        const on = this.state.boxes >= need;
        if (on) {
          unlocked += 1;
          value += Number(gift.dataset.giftValue) || 0;
        }

        gift.classList.toggle('is-locked', !on);
        const lock = gift.querySelector('[data-purchase-options-gift-lock]');
        if (lock) lock.textContent = on ? '' : (this.config.lockTemplate || '[n]+ BOXES').replace('[n]', need);
        const state = gift.querySelector('[data-purchase-options-gift-state]');
        if (state) state.textContent = on ? (this.config.freeLabel || 'FREE') : (this.config.lockedLabel || 'Locked');
      });

      const total = this.gifts.length || GIFT_TOTAL_FALLBACK;
      if (chip) {
        chip.textContent = unlocked > 0
          ? (this.config.chipWithGifts || '')
          : (this.config.chipWithoutGifts || '');
      }
      if (bar) bar.style.width = (total ? (unlocked / total) * 100 : 0) + '%';
      if (unlockedNode) {
        unlockedNode.textContent = (this.config.unlockedTemplate || 'Gifts unlocked: [n] / [total]')
          .replace('[n]', unlocked)
          .replace('[total]', total);
      }

      if (worth) {
        const basis = this.prices.full || this.prices.otp;
        const pct = basis > this.prices.sub ? Math.round((1 - this.prices.sub / basis) * 100) : 0;
        worth.innerHTML = unlocked > 0
          ? (this.config.worthTemplate || '')
              .replace('[pct]', pct)
              .replace('[n]', unlocked)
              .replace('[s]', unlocked > 1 ? 's' : '')
              .replace('[value]', this.formatMoney(value))
          : (this.config.worthEmptyTemplate || '').replace('[pct]', pct);
      }
    }

    syncPlanField() {
      const field = this.root.querySelector('[data-purchase-options-plan]');
      if (!field) return;

      const plan = this.currentPlan(this.variantNode(this.state.variantId));
      const value = this.state.purchase === 'sub' && plan ? String(plan.id) : '';

      field.innerHTML = '';
      const option = document.createElement('option');
      option.value = value;
      option.selected = true;
      field.appendChild(option);
      field.value = value;
    }

    setText(selector, text) {
      const node = this.root.querySelector(selector);
      if (node) node.textContent = text;
    }

    formatMoney(cents) {
      const value = Number(cents || 0) / 100;
      const format = this.config.moneyFormat || '${{amount}}';
      const placeholderMatch = format.match(/\{\{\s*(\w+)\s*\}\}/);

      const withDelimiters = (precision, thousands, decimal) => {
        const negative = value < 0 ? '-' : '';
        const parts = Math.abs(value).toFixed(precision).split('.');
        const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
        return negative + dollars + (parts[1] ? `${decimal}${parts[1]}` : '');
      };

      let amount;
      switch (placeholderMatch && placeholderMatch[1]) {
        case 'amount_no_decimals': amount = withDelimiters(0, ',', '.'); break;
        case 'amount_with_comma_separator': amount = withDelimiters(2, '.', ','); break;
        case 'amount_no_decimals_with_comma_separator': amount = withDelimiters(0, '.', ','); break;
        case 'amount_with_space_separator': amount = withDelimiters(2, ' ', ','); break;
        case 'amount_with_apostrophe_separator': amount = withDelimiters(2, "'", '.'); break;
        default: amount = withDelimiters(2, ',', '.');
      }

      const rendered = placeholderMatch ? format.replace(placeholderMatch[0], amount) : format;
      const parser = document.createElement('span');
      parser.innerHTML = rendered;
      return `${this.config.currencyPrefix || ''}${parser.textContent.trim()}`;
    }
  }

  // Read model for anything that needs to agree with this block.
  window.purchaseOptions = {
    instance(scope) {
      const root = (scope || document).querySelector(SELECTOR);
      return root?.purchaseOptionsInstance || null;
    },
    isSubscribing(scope) {
      return this.instance(scope)?.state.purchase === 'sub';
    },
    price(scope) {
      return this.instance(scope)?.currentPrice() ?? null;
    },
    boxes(scope) {
      return this.instance(scope)?.state.boxes ?? null;
    },
  };

  const mount = scope => scope.querySelectorAll(SELECTOR).forEach(root => {
    if (!root.purchaseOptionsInstance) root.purchaseOptionsInstance = new PurchaseOptions(root);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document));
  else mount(document);
  document.addEventListener('shopify:section:load', event => mount(event.target));
})();
